import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Tooltip } from "react-leaflet";
import { Link, useNavigate } from "react-router-dom";
import L from "leaflet";
import {
  Plus, Trash, ArrowsClockwise, Path, MagnifyingGlass, Flag,
  CaretRight, ArrowsLeftRight, FloppyDisk, Truck, ClipboardText, ListChecks,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { routesApi, fleetApi, jobsApi } from "@/lib/api";

function numberedIcon(n, color = "#ff8c00") {
  const html = `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;background:${color};color:#000;font-family:'JetBrains Mono',monospace;font-weight:900;font-size:12px;border:2px solid #000;border-radius:50%;box-shadow:0 0 0 2px ${color}66;">${n}</div>`;
  return L.divIcon({ html, className: "ll-numbered-marker", iconSize: [28, 28], iconAnchor: [14, 14] });
}

export default function RoutePlannerPage() {
  const [mode, setMode] = useState("jobs"); // "jobs" | "address"
  return (
    <div className="h-[calc(100vh-72px)] flex flex-col" data-testid="route-planner-page">
      <div className="px-6 py-3 border-b border-[#2a2a2a] flex items-center justify-between bg-[#0d0d0d]">
        <div className="flex items-center gap-2">
          <Path size={18} className="text-[#ff8c00]" weight="bold" />
          <h1 className="font-heading font-black text-lg tracking-tight">Route Planner</h1>
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] ml-3">TSP · nearest-neighbour + 2-opt</span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/routes/saved"
            className="border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4]/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2"
            data-testid="saved-routes-link"
          >
            <ListChecks size={11} weight="bold" /> Saved Routes
          </Link>
          <div className="flex border border-[#2a2a2a]">
            <button
              onClick={() => setMode("jobs")}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-1 ${mode === "jobs" ? "bg-[#ff8c00] text-black" : "text-[#a1a1aa] hover:bg-[#1a1a1a]"}`}
              data-testid="mode-jobs-btn"
            >
              <ClipboardText size={11} weight="bold" /> From Jobs
            </button>
            <button
              onClick={() => setMode("address")}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-1 ${mode === "address" ? "bg-[#ff8c00] text-black" : "text-[#a1a1aa] hover:bg-[#1a1a1a]"}`}
              data-testid="mode-address-btn"
            >
              <MagnifyingGlass size={11} weight="bold" /> Free-form
            </button>
          </div>
        </div>
      </div>
      {mode === "jobs" ? <JobsPlanner /> : <AddressPlanner />}
    </div>
  );
}

// ============================================================================
// Jobs-based planner (the real Monday-morning workflow)
// ============================================================================

function JobsPlanner() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [assetId, setAssetId] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [result, setResult] = useState(null);
  const [returnToStart, setReturnToStart] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fleetApi.list().then(setAssets).catch(() => setAssets([]));
    jobsApi.list().then(setJobs).catch(() => setJobs([]));
  }, []);

  // Filter to schedulable jobs that have coordinates set on either side
  const eligibleJobs = useMemo(() => {
    return jobs.filter((j) => {
      if (!["scheduled", "dispatched"].includes(j.status)) return false;
      const hasPickup = j.pickup_lat != null && j.pickup_lng != null;
      const hasDropoff = j.dropoff_lat != null && j.dropoff_lng != null;
      return hasPickup || hasDropoff;
    });
  }, [jobs]);

  const selectedJobs = useMemo(
    () => selectedJobIds.map((id) => jobs.find((j) => j.id === id)).filter(Boolean),
    [selectedJobIds, jobs],
  );

  const toggleJob = (id) => {
    setSelectedJobIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setResult(null);
  };

  // Build waypoint list: anchor at the truck's current location, then drop-off for each job
  const buildWaypoints = () => {
    const asset = assets.find((a) => a.id === assetId);
    const wps = [];
    if (asset && asset.lat != null && asset.lng != null) {
      wps.push({ id: "__start__", label: `${asset.name} (start)`, lat: asset.lat, lng: asset.lng });
    }
    for (const j of selectedJobs) {
      const useDropoff = j.dropoff_lat != null && j.dropoff_lng != null;
      wps.push({
        id: j.id,
        label: `${j.job_number} · ${j.customer}`,
        lat: useDropoff ? j.dropoff_lat : j.pickup_lat,
        lng: useDropoff ? j.dropoff_lng : j.pickup_lng,
      });
    }
    return wps;
  };

  const optimize = async () => {
    if (selectedJobs.length < 1) return toast.error("Pick at least 1 job");
    const wps = buildWaypoints();
    if (wps.length < 2) return toast.error("Need at least 2 points (asset start + 1 job). Add coords on the asset or job.");
    setBusy(true);
    try {
      const wpsWithStart = wps.length >= 2 ? wps : null;
      if (!wpsWithStart) throw new Error("not enough waypoints");
      const r = await routesApi.optimize({
        waypoints: wpsWithStart.slice(1),
        start: wpsWithStart[0],
        return_to_start: returnToStart,
      });
      setResult(r);
      toast.success(`Optimized — ${r.total_miles} mi (saved ${r.savings_pct}%)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Optimization failed");
    } finally {
      setBusy(false);
    }
  };

  const saveRoute = async () => {
    if (!result) return toast.error("Optimize first");
    if (!assetId) return toast.error("Pick a truck");
    setSaving(true);
    try {
      const asset = assets.find((a) => a.id === assetId);
      const stops = result.ordered.map((wp, i) => {
        const job = jobs.find((j) => j.id === wp.id);
        return {
          seq: i + 1,
          label: wp.label,
          lat: wp.lat,
          lng: wp.lng,
          job_id: job?.id,
          job_number: job?.job_number,
          customer: job?.customer,
          weight_lbs: job?.weight_lbs,
        };
      });
      const saved = await routesApi.create({
        name: `${asset?.name || "Route"} · ${date}`,
        route_date: date,
        asset_id: assetId,
        return_to_start: returnToStart,
        stops,
        total_miles: result.total_miles,
        naive_miles: result.naive_miles,
        savings_miles: result.savings_miles,
        savings_pct: result.savings_pct,
      });
      toast.success("Route saved");
      navigate(`/routes/saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save route");
    } finally {
      setSaving(false);
    }
  };

  const orderedToShow = result?.ordered || buildWaypoints();
  const polylinePts = orderedToShow.map((s) => [s.lat, s.lng]);
  if (returnToStart && polylinePts.length >= 2 && result) polylinePts.push(polylinePts[0]);
  const center = orderedToShow.length ? [orderedToShow[0].lat, orderedToShow[0].lng] : [47.6062, -122.3321];

  return (
    <div className="flex-1 flex overflow-hidden">
      <div className="w-[440px] border-r border-[#2a2a2a] bg-[#0d0d0d] flex flex-col">
        <div className="p-4 border-b border-[#2a2a2a] space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <select
              value={assetId}
              onChange={(e) => { setAssetId(e.target.value); setResult(null); }}
              className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-2 text-xs"
              data-testid="planner-asset-select"
            >
              <option value="">— Pick truck —</option>
              {assets.filter((a) => a.type === "truck").map((a) => (
                <option key={a.id} value={a.id}>{`${a.name} (${a.driver || "no driver"})`}</option>
              ))}
            </select>
            <input
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); setResult(null); }}
              className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-2 text-xs font-mono"
              data-testid="planner-date"
            />
          </div>
          <p className="text-[10px] text-[#71717a] leading-relaxed">
            Pick a truck + a date, tick the jobs to run, click <strong className="text-white">Optimize</strong>,
            then <strong className="text-white">Save Route</strong>. Drivers see the order in the Driver app.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          {eligibleJobs.length === 0 && (
            <div className="text-[11px] text-[#71717a] text-center py-6 font-mono">
              No jobs with coords yet. Add lat/lng on a job's pickup or dropoff first.
            </div>
          )}
          {eligibleJobs.map((j) => {
            const checked = selectedJobIds.includes(j.id);
            return (
              <label key={j.id} className={`block border p-2 cursor-pointer transition-colors ${checked ? "border-[#ff8c00] bg-[#ff8c00]/5" : "border-[#2a2a2a] hover:bg-[#1a1a1a]"}`} data-testid={`planner-job-${j.id}`}>
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleJob(j.id)}
                    className="mt-0.5 accent-[#ff8c00]"
                    data-testid={`planner-job-toggle-${j.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-bold text-xs flex items-center gap-2">
                      {j.job_number}
                      <span className="text-[9px] uppercase tracking-[0.2em] text-[#71717a]">{j.status}</span>
                      {j.route_id && <span className="text-[9px] uppercase tracking-[0.2em] text-[#22c55e]">on route</span>}
                    </div>
                    <div className="text-[11px] text-[#a1a1aa] truncate">{j.customer}</div>
                    <div className="text-[10px] text-[#71717a] font-mono">
                      {j.weight_lbs ? `${(j.weight_lbs / 1000).toFixed(1)}k lbs · ` : ""}
                      {(j.dropoff_lat != null) ? `→ ${j.dropoff_lat.toFixed(3)},${j.dropoff_lng.toFixed(3)}` : `← ${j.pickup_lat?.toFixed(3)},${j.pickup_lng?.toFixed(3)}`}
                    </div>
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="p-4 border-t border-[#2a2a2a] space-y-2">
          <label className="flex items-center gap-2 text-xs text-[#a1a1aa] cursor-pointer">
            <input
              type="checkbox"
              checked={returnToStart}
              onChange={(e) => { setReturnToStart(e.target.checked); setResult(null); }}
              className="accent-[#ff8c00]"
              data-testid="planner-return-to-start"
            />
            Return to start (close the loop)
          </label>
          {result && (
            <div className="border border-[#22c55e]/40 bg-[#22c55e]/5 px-3 py-2 text-[10px] font-mono space-y-0.5" data-testid="planner-result">
              <div className="text-[#22c55e] uppercase tracking-[0.25em] font-bold">Optimized</div>
              <div>Total: <strong className="text-white">{result.total_miles} mi</strong> · Stops: {result.stops}</div>
              <div>Naive: {result.naive_miles} mi</div>
              <div className="text-[#22c55e]">Saved: <strong>{result.savings_miles} mi ({result.savings_pct}%)</strong></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={optimize}
              disabled={busy || selectedJobs.length < 1 || !assetId}
              className="bg-[#ff8c00] hover:bg-[#ff8c00]/90 disabled:opacity-40 text-black font-bold py-2.5 text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-2"
              data-testid="planner-optimize-btn"
            >
              {busy ? <ArrowsClockwise size={12} className="animate-spin" /> : <Flag size={12} weight="bold" />}
              Optimize
            </button>
            <button
              onClick={saveRoute}
              disabled={saving || !result || !assetId}
              className="bg-[#22c55e] hover:bg-[#22c55e]/90 disabled:opacity-40 text-black font-bold py-2.5 text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-2"
              data-testid="planner-save-btn"
            >
              {saving ? <ArrowsClockwise size={12} className="animate-spin" /> : <FloppyDisk size={12} weight="bold" />}
              Save Route
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 relative">
        <MapContainer center={center} zoom={9} className="h-full w-full" key={`jobs-${center[0]}-${center[1]}-${orderedToShow.length}`}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {orderedToShow.map((s, i) => (
            <Marker key={s.id || i} position={[s.lat, s.lng]} icon={numberedIcon(i + 1, result ? "#ff8c00" : "#4682b4")}>
              <Tooltip>{s.label}</Tooltip>
            </Marker>
          ))}
          {polylinePts.length >= 2 && (
            <Polyline positions={polylinePts} pathOptions={{ color: result ? "#ff8c00" : "#4682b4", weight: 4, opacity: 0.85, dashArray: result ? null : "8 8" }} />
          )}
        </MapContainer>
      </div>
    </div>
  );
}

// ============================================================================
// Free-form address-based planner (existing functionality, slightly trimmed)
// ============================================================================

function AddressPlanner() {
  const [stops, setStops] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [returnToStart, setReturnToStart] = useState(false);
  const [result, setResult] = useState(null);

  const addStop = (s) => setStops((prev) => [...prev, s]);
  const removeStop = (i) => {
    setStops(stops.filter((_, idx) => idx !== i));
    setResult(null);
  };

  const geocode = async () => {
    const q = search.trim();
    if (!q) return;
    setBusy(true);
    try {
      const g = await routesApi.geocode(q, "us");
      addStop({ id: `${Date.now()}`, label: g.display_name?.split(",").slice(0, 2).join(",") || q, lat: g.lat, lng: g.lng });
      setSearch("");
      toast.success(`Added: ${g.display_name?.split(",").slice(0, 2).join(",")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Address not found");
    } finally {
      setBusy(false);
    }
  };

  const addManual = () => {
    const last = stops[stops.length - 1] || { lat: 47.6062, lng: -122.3321 };
    const jitter = (stops.length + 1) * 0.05;
    addStop({
      id: `${Date.now()}`,
      label: `Custom stop ${stops.length + 1}`,
      lat: +(last.lat + jitter).toFixed(4),
      lng: +(last.lng + jitter).toFixed(4),
    });
  };

  const optimize = async () => {
    if (stops.length < 2) return toast.error("Add at least 2 stops");
    setBusy(true);
    try {
      const r = await routesApi.optimize({ waypoints: stops, return_to_start: returnToStart });
      setResult(r);
      toast.success(`Optimized — ${r.total_miles} mi (saved ${r.savings_miles} mi · ${r.savings_pct}%)`);
    } catch (e) {
      toast.error("Optimization failed");
    } finally {
      setBusy(false);
    }
  };

  const orderedToShow = result?.ordered || stops;
  const polylinePts = orderedToShow.map((s) => [s.lat, s.lng]);
  if (returnToStart && polylinePts.length >= 2 && result) polylinePts.push(polylinePts[0]);
  const center = orderedToShow.length ? [orderedToShow[0].lat, orderedToShow[0].lng] : [47.6062, -122.3321];

  return (
    <div className="flex-1 flex overflow-hidden">
      <div className="w-[400px] border-r border-[#2a2a2a] bg-[#0d0d0d] flex flex-col">
        <div className="p-4 border-b border-[#2a2a2a] space-y-2">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#71717a]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && geocode()}
              placeholder="e.g. 4500 Airport Way S, Seattle WA"
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none pl-7 pr-2 py-2 text-xs font-mono"
              data-testid="route-search-input"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={geocode}
              disabled={busy || !search.trim()}
              className="flex-1 bg-[#ff8c00] hover:bg-[#ff8c00]/90 disabled:opacity-40 text-black font-bold px-3 py-2 text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-2"
              data-testid="route-geocode-btn"
            >
              {busy ? <ArrowsClockwise size={11} className="animate-spin" /> : <Plus size={11} weight="bold" />}
              Geocode & Add
            </button>
            <button
              onClick={addManual}
              className="border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4]/10 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-1"
              data-testid="route-add-manual-btn"
            >
              <Plus size={11} weight="bold" /> Custom
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          {orderedToShow.length === 0 && (
            <div className="text-[11px] text-[#71717a] text-center py-8 font-mono">
              No stops yet. Paste an address above to begin.
            </div>
          )}
          {orderedToShow.map((s, i) => (
            <div key={s.id || i} className="border border-[#2a2a2a] p-2 flex items-center gap-2" data-testid={`route-stop-${i}`}>
              <div className={`w-6 h-6 ${result ? "bg-[#ff8c00]" : "bg-[#4682b4]"} text-black font-mono font-black text-xs flex items-center justify-center`}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-xs truncate">{s.label || "Stop"}</div>
                <div className="text-[10px] font-mono text-[#71717a]">{s.lat.toFixed(4)}, {s.lng.toFixed(4)}</div>
              </div>
              {!result && (
                <button onClick={() => removeStop(i)} className="text-[#ef4444] hover:bg-[#ef4444]/10 p-1" data-testid={`route-stop-remove-${i}`}>
                  <Trash size={11} />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-[#2a2a2a] space-y-2">
          <label className="flex items-center gap-2 text-xs text-[#a1a1aa] cursor-pointer">
            <input
              type="checkbox"
              checked={returnToStart}
              onChange={(e) => { setReturnToStart(e.target.checked); setResult(null); }}
              className="accent-[#ff8c00]"
              data-testid="route-return-to-start"
            />
            Return to start
          </label>
          {result && (
            <div className="border border-[#22c55e]/40 bg-[#22c55e]/5 px-3 py-2 text-[10px] font-mono space-y-0.5" data-testid="route-results">
              <div className="text-[#22c55e] uppercase tracking-[0.25em] font-bold">Optimized</div>
              <div>Total: <strong className="text-white">{result.total_miles} mi</strong></div>
              <div>Saved: <strong className="text-[#22c55e]">{result.savings_miles} mi ({result.savings_pct}%)</strong></div>
            </div>
          )}
          {!result ? (
            <button
              onClick={optimize}
              disabled={busy || stops.length < 2}
              className="w-full bg-[#ff8c00] hover:bg-[#ff8c00]/90 disabled:opacity-40 text-black font-bold py-3 text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-2"
              data-testid="route-optimize-btn"
            >
              {busy ? <ArrowsClockwise size={12} className="animate-spin" /> : <Flag size={12} weight="bold" />}
              Optimize Route
            </button>
          ) : (
            <button
              onClick={() => setResult(null)}
              className="w-full border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4]/10 py-3 text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-2"
              data-testid="route-reset-btn"
            >
              <ArrowsLeftRight size={12} weight="bold" /> Edit Stops
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 relative">
        <MapContainer center={center} zoom={9} className="h-full w-full" key={`addr-${center[0]}-${center[1]}-${orderedToShow.length}`}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {orderedToShow.map((s, i) => (
            <Marker key={s.id || i} position={[s.lat, s.lng]} icon={numberedIcon(i + 1, result ? "#ff8c00" : "#4682b4")}>
              <Tooltip>{s.label}</Tooltip>
            </Marker>
          ))}
          {polylinePts.length >= 2 && (
            <Polyline positions={polylinePts} pathOptions={{ color: result ? "#ff8c00" : "#4682b4", weight: 4, opacity: 0.85, dashArray: result ? null : "8 8" }} />
          )}
        </MapContainer>
      </div>
    </div>
  );
}
